import { track } from "../../clients/mixpanel";
import { fetchFloorAreasWithGroups } from "../reports/areaSheet";
import { fetchEstimateExport } from "../../api/estimates";
import { useGroupAreaLabel } from "../datasources/floorAreas";
import { getEstimateReportData } from "../workbook";
import { NotFoundError } from "../../errors";
import {
  CHART_PREFIX,
  colorizeChart,
  getChartAndTable,
  getChartDimensions,
  getNextChartIndex,
  getTableTopLeftCellValue,
  optionsAsEventData,
  setChartTitleAndSubtitle,
  upsertChartTable,
} from "./helpers/common";
import {
  ChartOption,
  ChartOptionItem,
  ChartShape,
  ChartSize,
  EdiphiChartType,
  ChartIcon,
  ChartIconGetter,
} from "./api";

const AREA_CHART_TYPE = "areaSheet";
const blankBuildingOptionItem = { key: "unassigned", label: "unassigned" };

const getFirstNonBlankBuilding = (buildingOptionItems: Record<string, ChartOptionItem>): ChartOptionItem => {
  return (
    Object.entries(buildingOptionItems)
      .filter(([_, value]) => value.key !== blankBuildingOptionItem.key)
      .map(([_, value]) => ({
        key: value.key,
        label: value.label,
      }))[0] || blankBuildingOptionItem
  );
};

const areaSheetOptions: Record<string, ChartOption> = {
  building: {
    key: "building",
    label: "Building",
    type: "dropDown",
    selectedOptionItem: blankBuildingOptionItem,
    optionItems: {},
    getSelectedOptionItem: async function (_, chartName) {
      const buildingOptionItems = await getBuildingOptionItems();
      this.optionItems = buildingOptionItems;
      const buildingName = await getTableTopLeftCellValue(chartName);
      return this.optionItems[buildingName] || blankBuildingOptionItem;
    },
    onChange: async function (worksheetName, chartName, optionItem) {
      const options = { ...areaSheetOptions };
      options[this.key].selectedOptionItem = optionItem;
      await chartRefresher(worksheetName, chartName, options);
    },
  },
  dimensions: {
    key: "dimensions",
    label: "Dimension",
    type: "toggle",
    selectedOptionItem: {
      key: "3D",
      label: "3D",
      icon: { src: "assets/icons/FcCubeDuotone.svg", alt: "3D Cube" },
    },
    optionItems: {
      "2D": {
        key: "2D",
        label: "2D",
        icon: { src: "assets/icons/FcSquareDuotone.svg", alt: "2D Square" },
      },
      "3D": {
        key: "3D",
        label: "3D",
        icon: { src: "assets/icons/FcCubeDuotone.svg", alt: "3D Cube" },
      },
    },
    getSelectedOptionItem: async function (worksheetName, chartName) {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.load("chartType");
        await context.sync();
        return chart.chartType === Excel.ChartType._3DBarStacked ? this.optionItems["3D"] : this.optionItems["2D"];
      });
    },
    onChange: async function (worksheetName, chartName, optionItem) {
      return Excel.run(async (context) => {
        const [chart] = await getChartAndTable(context, worksheetName, chartName);
        chart.chartType = optionItem.key === "3D" ? Excel.ChartType._3DBarStacked : Excel.ChartType.barStacked;
        await context.sync();
      });
    },
  },
};

const chartBuilder = async (options: Record<string, ChartOption>) => {
  const startTime = performance.now();
  const buildingsOptionItems = await getBuildingOptionItems();
  const building = getFirstNonBlankBuilding(buildingsOptionItems);

  // if building option is not set, set it
  if (!options.building) {
    options.building = {
      key: "building",
      label: "Building",
      type: "dropDown",
      selectedOptionItem: building,
      optionItems: {},
    };
  }
  const chartIndex = await getNextChartIndex();
  const chartName = `${CHART_PREFIX}${AREA_CHART_TYPE}_${chartIndex}`;

  await upsertChartData(options, chartName);
  const chart = await addChart({
    chartType: Excel.ChartType._3DBarStacked,
    chartTitle: "Area Sheet",
    chartSubTitle: building.label || "",
    name: chartName,
    shape: "photoPortrait",
    size: "large",
  });

  track("excel.chart.create", {
    chartType: AREA_CHART_TYPE,
    loadTime: (performance.now() - startTime) / 1000,
    ...optionsAsEventData(options),
  });
  return chart;
};

const chartRefresher = async (worksheetName: string, chartName: string, options?: Record<string, ChartOption>) => {
  const buildingOptionItems = await getBuildingOptionItems();
  let selectedBuildingOptionItem = options?.building.selectedOptionItem || blankBuildingOptionItem;

  // If selected building not in options, get first non-blank building or blank if none exist
  if (!buildingOptionItems[selectedBuildingOptionItem.key]) {
    selectedBuildingOptionItem = getFirstNonBlankBuilding(buildingOptionItems);
    if (options) {
      options.building.selectedOptionItem = selectedBuildingOptionItem;
    }
  }

  await upsertChartData(options || {}, chartName);

  await Excel.run(async (context) => {
    const [chart, table] = await getChartAndTable(context, worksheetName, chartName);
    chart.setData(table.getRange());
    await context.sync();
    const subTitle =
      selectedBuildingOptionItem.key === blankBuildingOptionItem.key ? "" : selectedBuildingOptionItem.label;
    setChartTitleAndSubtitle(chart, "Area Sheet", subTitle);
    await colorizeChart(chart, context);
    await context.sync();
  });
};

const upsertChartData = async (options: Record<string, ChartOption>, chartName: string) => {
  const buildingOptionItem = options["building"]?.selectedOptionItem;
  const floorGroup = buildingOptionItem?.key || blankBuildingOptionItem.key;

  const createDataRows = async () => {
    const data = await fetchFloorAreasWithGroups();
    let floorRecords = data;
    floorRecords = data.filter((row) => row?.floor_group === floorGroup && row?.type !== "FEATURE");

    const floorKeys = new Set(floorRecords.flatMap((floor) => Object.keys(floor)));
    const floorUseGroups = Array.from(floorKeys)
      .filter(
        (key) =>
          key.endsWith("_area") && key !== "total_area" && key !== "structure_area" && !key.endsWith("_exclude_area")
      )
      .map((key) => key.replace("_area", ""));
    const assignedUseGroups = await getAssignedUseGroupLabels(floorGroup);
    const valueProperties = Array.from(new Set([...floorUseGroups, ...assignedUseGroups]));
    const dataRows: Record<string, string | number | boolean | Date | null | undefined>[] = [];
    for (const floor of floorRecords) {
      const dataRow: Record<string, string | number | boolean | Date | null | undefined> = {};
      dataRow[floorGroup] = floor.label;
      for (const property of valueProperties) {
        dataRow[property] = floor[`${property}_area`] || "";
      }
      dataRows.push(dataRow);
    }

    return dataRows;
  };

  const dataRows = await createDataRows();
  await upsertChartTable(dataRows, chartName);
};

const getAssignedUseGroupLabels = async (building: string): Promise<string[]> => {
  const reportData = await getEstimateReportData();
  const estimateId = reportData[0]?.estimate_id;
  if (!estimateId) return [];
  const result = await fetchEstimateExport(estimateId);
  if (!result.success || !result.data) return [];

  const { estimate, floor_groups } = result.data;
  const buildingId = floor_groups?.find((floorGroup) => floorGroup.label === building)?.id;
  if (!buildingId) return [];
  return (estimate.stats.useGroups || [])
    .filter((useGroup) => useGroup.floor_group === buildingId && !useGroup.exclude_area)
    .map(useGroupAreaLabel);
};

const addChart = async (params: {
  chartType: Excel.ChartType;
  chartTitle: string;
  chartSubTitle: string;
  name: string;
  shape: ChartShape;
  size: ChartSize;
}) => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const activeSheet = workbook.worksheets.getActiveWorksheet();
    const table = workbook.tables.getItemOrNullObject(params.name);
    await context.sync();
    if (table.isNullObject) {
      throw new NotFoundError("Table not found", { tableName: params.name });
    }

    const range = table.getRange();
    await context.sync();
    const chart = activeSheet.charts.add(params.chartType, range, Excel.ChartSeriesBy.columns);
    chart.name = params.name;
    const subTitle = params.chartSubTitle === blankBuildingOptionItem.key ? "" : params.chartSubTitle;
    setChartTitleAndSubtitle(chart, params.chartTitle, subTitle);

    const { width, height } = getChartDimensions(params.shape, params.size);
    chart.height = height;
    chart.width = width;
    styleChart(chart);
    await colorizeChart(chart, context);
    await context.sync();
    return chart;
  });
};

const styleChart = (chart: Excel.Chart) => {
  chart.axes.categoryAxis.format.line.lineStyle = Excel.ChartLineStyle.none;
  chart.axes.categoryAxis.reversePlotOrder = true;
  chart.axes.categoryAxis.format.font.size = 11;
  chart.axes.categoryAxis.format.font.bold = true;
  chart.axes.valueAxis.format.font.size = 10;
  chart.axes.valueAxis.numberFormat = '[>999999]0.0,,"M sf";[>999]0,"k sf";0';
  chart.legend.visible = true;
  chart.legend.position = Excel.ChartLegendPosition.bottom;
  chart.legend.format.font.size = 12;
  chart.legend.format.font.bold = true;
  const series = chart.series.getItemAt(0);
  series.gapWidth = 5;
};

const getOptions = async (worksheetName: string, chartName: string): Promise<Record<string, ChartOption>> => {
  const options = { ...areaSheetOptions };

  for (const [key, option] of Object.entries(options)) {
    if (option.getSelectedOptionItem) {
      try {
        const currentValue = await option.getSelectedOptionItem(worksheetName, chartName);
        // Guarded because these option objects are shared: the spread above is shallow, so writing an
        // unresolved value here would replace the declared default until the module is re-imported.
        if (currentValue) options[key].selectedOptionItem = currentValue;
      } catch (error) {
        // ???: Change to logger.warn?
        console.warn(`Failed to get current value for option ${key}:`, error);
      }
    }
  }

  return options;
};

const getBuildingOptionItems = async (): Promise<Record<string, ChartOptionItem>> => {
  const data = await fetchFloorAreasWithGroups();
  const floors = data.filter((row) => row.type !== "FEATURE");
  const floorGroups = new Set<string>();
  floors.forEach((floor) => {
    const floorGroup = floor.floor_group?.toString();
    if (floorGroup === "") {
      floorGroups.add(blankBuildingOptionItem.key);
    } else {
      floorGroups.add(floorGroup);
    }
  });

  const items: Record<string, ChartOptionItem> = {};
  floorGroups.forEach((group) => {
    if (group === blankBuildingOptionItem.key) {
      items[group] = blankBuildingOptionItem;
    } else {
      items[group] = { key: group, label: group };
    }
  });

  return items;
};

const chartIconGetter: ChartIconGetter = (chartType?: Excel.ChartType): ChartIcon => {
  const chartTypeLower = chartType?.toString().toLowerCase() || "3d";
  if (chartTypeLower.includes("3d")) {
    return { src: "assets/icons/FcCubeDuotone.svg", alt: "3D Cube" };
  } else {
    return { src: "assets/icons/FcSquareDuotone.svg", alt: "2D Square" };
  }
};

export const areaSheetChartType: EdiphiChartType = {
  key: AREA_CHART_TYPE,
  title: "Area Sheet",
  chartBuilder,
  chartRefresher,
  chartIconGetter,
  getOptions,
};
